import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Stack,
  Backdrop,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import { useLocation } from "react-router-dom";
import StorageFactory from "../../../Util/StorageFactory";
import { SESSION_USER_ID_KEY,STATUS_COMPLETED, STATUS_INPROGRESS,STATUS_PENDING } from "../../../Util/VMConstants";
import {  API_ADD_TASK_UPDATE, API_GET_ASSIGNED_TASKS,API_UPLOAD_TASK_IMG } from "../../../Util/URLConstants";
import { align_center, dataGridStyle, VM_Colors } from "../../../Styles/VMStyles";
import MUITypography from "../../../Components/MUITypography";
import { toast } from "react-toastify";
import { formatDate, getPriorityColor } from "../../../Util/ConstnatUtils";
import StatusButton from "../../../Components/StatusButton";
import { CalendarToday } from "@mui/icons-material";
import MuiTypographyMandatory from "../../../Components/Custom/MuiTypographyMandatory";
import StyledInputField from "../../../Components/Custom/StyledInputField";
import { isEmpty } from "../../../Util/UtilHelper";
import ReusableDataGrid from "../../../Components/ReusableDataGrid";

const taskGridSx = {
  ...dataGridStyle,
  border: "none",
  "& .MuiDataGrid-root": { border: "none" },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: VM_Colors.primary,
    minHeight: "48px !important",
    maxHeight: "48px !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even)": {
    backgroundColor: VM_Colors.white2,
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(30, 58, 138, 0.06) !important",
  },
  "& .MuiDataGrid-cell": {
    ...dataGridStyle["& .MuiDataGrid-cell"],
    alignItems: "center",
    py: 1,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${VM_Colors.light_gray}`,
    minHeight: 52,
  },
};

function TaskUpdate() {
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [status, setStatus] = useState("");
  const [comments, setComments] = useState("");
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const deepLinkTaskId = useMemo(() => {
    const fromState = location?.state?.taskId;
    const fromQuery = new URLSearchParams(location?.search || "").get("taskId");
    return fromState || fromQuery || "";
  }, [location?.state, location?.search]);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setGridLoading(true);
      const res = await axios.get(`${API_GET_ASSIGNED_TASKS}?user_id=${userID}`);
      const formattedData = (res.data?.data || []).map((item, idx) => ({
        ...item,
        s_no: idx + 1,
      }));
      setTasks(formattedData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to fetch tasks");
    } finally {
      setGridLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    if (userID) fetchTasks();
  }, [userID, fetchTasks]);

  useEffect(() => {
    if (!deepLinkTaskId || !tasks.length || openDialog) return;
    const match = tasks.find((t) => String(t.id) === String(deepLinkTaskId));
    if (match) {
      setSelectedTask(match);
      setComments("");
      setImage(null);
      setOpenDialog(true);
    }
  }, [deepLinkTaskId, tasks, openDialog]);

  const handleOpenDialog = useCallback((task) => {
    setSelectedTask(task);
    setComments("");
    setImage(null);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = () => {
    if (submitting) return; // Prevent closing while submitting
    setOpenDialog(false);
    setSelectedTask(null);
    setStatus("");
    setComments("");
    setImage(null);
  };

  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  // Submit Task Update
  const handleSubmitUpdate = async () => {
    if (!status) {
      toast.error("Please select a status.");
      return;
    }
    if (isEmpty(comments)) {
      toast.error("Please enter comments.");
      return;
    }
    if (!selectedTask?.id) {
      toast.error("No task selected.");
      return;
    }

    let uploadedImageUrl = null;

    try {
      setSubmitting(true);

      // Upload image to S3 if provided
      if (image) {
        const formData = new FormData();
        formData.append("file", image);
        formData.append("task_id", selectedTask.id);
        formData.append("user_id", userID);

        const uploadRes = await axios.post(API_UPLOAD_TASK_IMG, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadRes.data?.status_code === 200) {
          uploadedImageUrl = uploadRes.data?.data?.file_url;
        } else {
          throw new Error(uploadRes.data?.message || "Image upload failed");
        }
      }

      // Task update API
      const reqBody = {
        user_id: userID,
        vm_tasks_id: selectedTask.id,
        task_status: status,
        comments: comments?.trim() || null,
        image_url: uploadedImageUrl,
      };

      const { data } = await axios.post(API_ADD_TASK_UPDATE, reqBody, {
        headers: { "Content-Type": "application/json" },
      });

      if (data?.status_code === 200) {
        toast.success(" Task updated successfully!");
        handleCloseDialog();
        fetchTasks();
      } else {
        throw new Error(data?.message || "Failed to update task.");
      }
    } catch (error) {
      console.error("❌ Error updating task:", error);
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          "Failed to update task."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderTextCell = (value) => (
    <Typography
      variant="body2"
      sx={{
        lineHeight: 1.4,
        wordBreak: "break-word",
        color: value ? "text.primary" : "text.disabled",
      }}
    >
      {value || "—"}
    </Typography>
  );

  const columns = useMemo(
    () => [
      {
        field: "s_no",
        headerName: "S.No",
        width: 64,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "name",
        headerName: "Task Name",
        flex: 1.4,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => renderTextCell(params.value),
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 118,
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={params.value || "—"}
            color={getPriorityColor(params.value)}
            variant="outlined"
            size="small"
            sx={{ minWidth: 88, fontWeight: 600, fontSize: "0.75rem" }}
          />
        ),
      },
      {
        field: "task_status",
        headerName: "Status",
        width: 130,
        sortable: false,
        renderCell: (params) => <StatusButton status={params.value} />,
      },
      {
        field: "due_date",
        headerName: "Due Date",
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2">
              {params.value ? formatDate(params.value) : "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "actions",
        headerName: "Update",
        width: 88,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Tooltip title="Update task">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
              aria-label="Update task"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleOpenDialog]
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        maxWidth: "100%",
        m: { xs: 1, sm: 2 },
      }}
    >
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        My Tasks
      </Typography>

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          bgcolor: "#fff",
          borderRadius: 2,
          border: `1px solid ${VM_Colors.light_gray}`,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.08)",
        }}
      >
        <ReusableDataGrid
          rows={tasks}
          columns={columns}
          loading={gridLoading}
          getRowId={(row) => row.id}
          paginationMode="client"
          rowCount={tasks.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          pageSizeOptions={[5, 10, 25]}
          autoHeight
          getRowHeight={() => 56}
          customSx={taskGridSx}
        />
      </Box>

      {/* Update Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: VM_Colors.primary }}>
          <MUITypography label ='Update Task' variant='h3' sx={{...align_center,fontWeight:700}}/>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MuiTypographyMandatory label="Task:" />
                <MUITypography
                  fontWeight={700}
                  label={selectedTask.name}
                  sx={{ color: VM_Colors.primary }}
                />
              </Stack>

              <Box mt={3}>
                <MuiTypographyMandatory label="Status" required />
                <FormControl fullWidth margin="normal">
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      selected ? (
                        // show human readable label for selected status
                        selected === STATUS_INPROGRESS
                          ? "In Progress"
                          : selected === STATUS_COMPLETED
                          ? "Complete"
                          : selected === STATUS_PENDING
                          ? "Incomplete"
                          : selected
                      ) : (
                        // placeholder / hint when no value selected
                        <span style={{ color: "#9e9e9e" }}>Select status</span>
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      <em>Select status</em>
                    </MenuItem>
                    <MenuItem value={STATUS_INPROGRESS}>In Progress</MenuItem>
                    <MenuItem value={STATUS_COMPLETED}>Complete</MenuItem>
                    <MenuItem value={STATUS_PENDING}>Incomplete</MenuItem>
                  </Select>
                </FormControl>

                <MuiTypographyMandatory label="Comments" required />
                <StyledInputField
                  multiline
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Box>

              <Stack mt={2} direction="row" alignItems="center">
                <Button variant="outlined" component="label">
                  Upload Image (Optional)
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {image && (
                  <MUITypography
                    variant="body2"
                    sx={{ ...align_center, ml: 2 }}
                  >
                    {image.name}
                  </MUITypography>
                )}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitUpdate}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {submitting ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop while submitting */}
      <Backdrop
        open={submitting}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ ml: 2 }}>Submitting update...</Typography>
      </Backdrop>
    </Paper>
  );
}

export default TaskUpdate;
